import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { getMyEvents, deleteEvent, setEventEnabled, EventResponse, isEventExpired, isEventDateExpired, formatEventRange } from "../../services/event-service"
import { getGigsByEvent } from "../../services/gig-service"
import ConfirmModal from "../../components/confirmModal"

export const EventsPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [events, setEvents] = useState<EventResponse[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const [pendingDelete, setPendingDelete] = useState<EventResponse | null>(null)
    const [pendingDeleteJobs, setPendingDeleteJobs] = useState<number | null>(null)
    const [togglingId, setTogglingId] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)
    const [flash, setFlash] = useState<string | null>(null)
    const [pressedId, setPressedId] = useState<string | null>(null)

    useEffect(() => {
        loadData()
    }, [])

    // mesaj de succes după creare/editare (trimis prin navigate state)
    useEffect(() => {
        const msg = (location.state as { flash?: string } | null)?.flash
        if (msg) {
            setFlash(msg)
            navigate(location.pathname, { replace: true, state: null })
        }
    }, [location, navigate])

    useEffect(() => {
        if (!flash) return
        const t = setTimeout(() => setFlash(null), 4000)
        return () => clearTimeout(t)
    }, [flash])

    useEffect(() => {
        if (!notice) return
        const t = setTimeout(() => setNotice(null), 6000)
        return () => clearTimeout(t)
    }, [notice])

    const loadData = async () => {
        setIsLoading(true)
        try {
            const eventsData = await getMyEvents()
            setEvents(eventsData)
        } catch {
            // ignore
        }
        setIsLoading(false)
    }

    const openDelete = (ev: EventResponse) => {
        setPendingDelete(ev)
        setPendingDeleteJobs(null)
        getGigsByEvent(ev.id)
            .then(g => setPendingDeleteJobs(Array.isArray(g) ? g.length : 0))
            .catch(() => setPendingDeleteJobs(0))
    }

    const handleDelete = async (id: string) => {
        setDeletingId(id)
        try {
            await deleteEvent(id)
            setEvents(prev => prev.filter(ev => ev.id !== id))
            setPendingDelete(null)
        } catch (e: any) {
            setPendingDelete(null)
            setNotice(e?.message || "Nu am putut șterge evenimentul. Încearcă din nou.")
        }
        setDeletingId(null)
    }

    const handleToggleEnabled = async (ev: EventResponse) => {
        const next = ev.enabled === false
        if (next && isEventDateExpired(ev)) {
            setNotice(`„${ev.title}” a expirat și nu poate fi reactivat direct. Apasă pe „Editează” și schimbă perioada evenimentului pentru a-l publica din nou.`)
            return
        }
        setNotice(null)
        setTogglingId(ev.id)
        try {
            const updated = await setEventEnabled(ev.id, next)
            setEvents(prev => prev.map(e => e.id === updated.id ? updated : e))
        } catch (e: any) {
            setNotice(e?.message || "A apărut o eroare. Încearcă din nou.")
        }
        setTogglingId(null)
    }

    const activeEvents = events.filter(e => !isEventExpired(e))
    const inactiveEvents = events.filter(e => isEventExpired(e))

    const renderEventCard = (ev: EventResponse) => {
        const isInactive = ev.enabled === false
        const isDateExpired = isEventDateExpired(ev)
        const muted = isInactive || isDateExpired
        const statusLabel = isInactive ? "Inactiv" : isDateExpired ? "Expirat" : null
        const pressed = pressedId === ev.id
        return (
            <div
                key={ev.id}
                role="button"
                tabIndex={0}
                aria-label={`Vezi detalii pentru ${ev.title}`}
                className={`group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 will-change-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-figmaPrimary focus-visible:ring-offset-2 ${
                    muted
                        ? `border shadow-sm hover:shadow-md hover:border-figmaDarkGray ${pressed ? "border-figmaDarkGray shadow-md" : "border-figmaDarkBg"}`
                        : `border shadow-sm hover:shadow-lg hover:border-figmaPrimary hover:-translate-y-1 ${pressed ? "border-figmaPrimary shadow-lg -translate-y-1" : "border-figmaLightGray"}`
                }`}
                onClick={() => navigate(`/events/${ev.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/events/${ev.id}`) } }}
                onPointerDown={() => setPressedId(ev.id)}
                onPointerUp={() => setPressedId(null)}
                onPointerLeave={() => setPressedId(null)}
                onPointerCancel={() => setPressedId(null)}
            >
                <div className="relative h-44 w-full">
                    {ev.imageUrl ? (
                        <img src={ev.imageUrl} alt={ev.title} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-figmaPrimary/10 to-figmaPrimary/30 text-3xl">🗓️</div>
                    )}

                    {/* voal subtil pentru evenimentele inactive/expirate — distinctiv, nu desaturare totală */}
                    {muted && <div className="absolute inset-0 bg-white/45" />}

                    {/* badge-uri (sus-stânga): status + promovat */}
                    <div className="absolute top-3 left-3 flex flex-col items-start gap-2">
                        {statusLabel && (
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-white shadow-md backdrop-blur-sm ${
                                isInactive ? "bg-figmaDarkGray/90" : "bg-figmaContrast/90"
                            }`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                {statusLabel}
                            </span>
                        )}
                        {ev.promoted && !muted && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-figmaPrimary shadow-md">
                                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" /></svg>
                                Promovat
                            </span>
                        )}
                    </div>

                    {/* acțiuni rapide (sus-dreapta) — creion + coș */}
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button
                            type="button"
                            aria-label="Editează evenimentul"
                            title="Editează"
                            onClick={(e) => { e.stopPropagation(); navigate(`/events/${ev.id}/edit`, { state: { from: "/events" } }) }}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-figmaPrimary shadow-md backdrop-blur-sm transition-colors hover:bg-figmaPrimary hover:text-white"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            aria-label="Șterge evenimentul"
                            title="Șterge"
                            disabled={deletingId === ev.id}
                            onClick={(e) => { e.stopPropagation(); openDelete(ev) }}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-figmaContrast shadow-md backdrop-blur-sm transition-colors hover:bg-figmaContrast hover:text-white disabled:opacity-50"
                        >
                            {deletingId === ev.id ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
                                    <path d="M3 6h18" />
                                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                    <path d="M10 11v6M14 11v6" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                <div className="p-5">
                    <h5 className="font-bold text-figmaBlack text-base leading-snug">{ev.title}</h5>

                    {ev.description && (
                        <p className="text-figmaDarkGray text-sm mt-2 line-clamp-2">{ev.description}</p>
                    )}

                    <div className="flex flex-wrap gap-2 mt-3">
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-figmaLightGray px-2.5 py-1.5 text-xs font-medium text-figmaBlack">
                            <svg viewBox="0 0 24 24" fill="none" stroke="#486af6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <path d="M16 2v4M8 2v4M3 10h18" />
                            </svg>
                            {formatEventRange(ev.dateFrom, ev.dateTo)}
                        </span>
                        {(ev.cityName || ev.address) && (
                            <span className="inline-flex items-center gap-1.5 rounded-lg bg-figmaLightGray px-2.5 py-1.5 text-xs font-medium text-figmaBlack">
                                <svg viewBox="0 0 24 24" fill="none" stroke="#486af6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                {[ev.address, ev.cityName && `${ev.cityName}${ev.countyCode ? ` (${ev.countyCode})` : ""}`].filter(Boolean).join(", ")}
                            </span>
                        )}
                    </div>

                    <div className="mt-4 border-t border-figmaLightGray pt-4">
                        {isDateExpired ? (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate(`/events/${ev.id}/edit`, { state: { from: "/events", republish: true } }) }}
                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-figmaPrimary px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                            >
                                ♻️ Republică evenimentul
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleToggleEnabled(ev) }}
                                disabled={togglingId === ev.id}
                                className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
                                    isInactive
                                        ? "bg-figmaPrimary text-white hover:opacity-90"
                                        : "bg-figmaLightGray text-figmaDarkGray hover:bg-figmaDarkBg/30 hover:text-figmaBlack"
                                }`}
                            >
                                {togglingId === ev.id ? (
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                ) : (
                                    <>
                                        <span className={`h-2 w-2 rounded-full ${isInactive ? "bg-white" : "bg-figmaDarkGray"}`} />
                                        {isInactive ? "Activează evenimentul" : "Dezactivează evenimentul"}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    const sectionLabel = (text: string, count: number) => (
        <div className="flex items-center gap-3 px-1">
            <span className="text-xs font-bold uppercase tracking-wider text-figmaPrimary">{text}</span>
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-figmaLightGray px-1.5 text-[11px] font-bold text-figmaDarkGray">{count}</span>
            <div className="h-px flex-1 bg-figmaLightGray" />
        </div>
    )

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 py-4 sm:px-6 sm:py-6 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="page-title text-[24px] sm:text-[28px] md:text-[32px]">Evenimente</h3>
                        <p className="page-description text-sm sm:text-base">Evenimentele postate de tine</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate("/events/new")}
                        className="bg-figmaPrimary text-white font-bold rounded-2xl px-5 py-3 text-sm hover:opacity-90 transition-opacity"
                    >
                        + Eveniment nou
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-6 sm:px-6">

                {flash && (
                    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-white border border-figmaPrimary rounded-2xl px-5 py-4 shadow-lg text-figmaBlack text-sm font-medium flex items-center gap-2">
                        <span className="text-figmaPrimary">✅</span> {flash}
                    </div>
                )}

                {notice && (
                    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-white border border-figmaDarkGray rounded-2xl px-5 py-4 shadow-lg text-figmaBlack text-sm font-medium flex items-start justify-between gap-3">
                        <span>ℹ️ {notice}</span>
                        <button
                            type="button"
                            onClick={() => setNotice(null)}
                            className="text-figmaDarkGray hover:text-figmaBlack font-bold shrink-0"
                            aria-label="Închide"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {isLoading ? (
                    <div className="flex flex-col gap-5">
                        {[0, 1, 2].map(i => <EventCardSkeleton key={i} />)}
                    </div>
                ) : events.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center text-figmaDarkGray">
                        <span className="text-5xl">🗓️</span>
                        <p className="text-lg font-semibold text-figmaBlack mt-3">Niciun eveniment postat</p>
                        <p className="text-sm mt-1 max-w-xs">Creează primul tău eveniment și începe să atragi candidați.</p>
                        <button
                            type="button"
                            onClick={() => navigate("/events/new")}
                            className="mt-5 bg-figmaPrimary text-white font-bold rounded-2xl px-5 py-3 text-sm hover:opacity-90 transition-opacity"
                        >
                            + Creează primul eveniment
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-8">
                        {activeEvents.length > 0 && (
                            <section className="flex flex-col gap-5">
                                {inactiveEvents.length > 0 && sectionLabel("Active", activeEvents.length)}
                                {activeEvents.map(renderEventCard)}
                            </section>
                        )}
                        {inactiveEvents.length > 0 && (
                            <section className="flex flex-col gap-5">
                                {sectionLabel("Inactive / Expirate", inactiveEvents.length)}
                                {inactiveEvents.map(renderEventCard)}
                            </section>
                        )}
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={!!pendingDelete}
                title="Ștergi acest eveniment?"
                message={
                    pendingDeleteJobs === null
                        ? "Se verifică joburile asociate..."
                        : pendingDeleteJobs > 0
                            ? `Acest eveniment are ${pendingDeleteJobs} ${pendingDeleteJobs === 1 ? "job asociat" : "joburi asociate"}. Dacă îl ștergi, ${pendingDeleteJobs === 1 ? "și acel job va deveni inaccesibil" : "și acele joburi vor deveni inaccesibile"}. Această acțiune nu poate fi anulată.`
                            : pendingDelete ? `„${pendingDelete.title}” va fi șters definitiv. Această acțiune nu poate fi anulată.` : undefined
                }
                confirmText="Șterge oricum"
                loading={!!pendingDelete && deletingId === pendingDelete.id}
                onConfirm={() => pendingDelete && handleDelete(pendingDelete.id)}
                onCancel={() => { setPendingDelete(null); setPendingDeleteJobs(null) }}
            />
        </div>
    )
}

const EventCardSkeleton = () => (
    <div className="bg-white rounded-2xl overflow-hidden border border-figmaLightGray shadow-sm animate-pulse">
        <div className="h-44 w-full bg-figmaLightGray" />
        <div className="p-5">
            <div className="h-4 w-2/3 rounded bg-figmaLightGray" />
            <div className="mt-3 h-3 w-full rounded bg-figmaLightGray" />
            <div className="mt-2 h-3 w-4/5 rounded bg-figmaLightGray" />
            <div className="mt-4 flex gap-2">
                <div className="h-7 w-40 rounded-lg bg-figmaLightGray" />
                <div className="h-7 w-28 rounded-lg bg-figmaLightGray" />
            </div>
            <div className="mt-4 h-10 w-full rounded-xl bg-figmaLightGray" />
        </div>
    </div>
)
